import { useState, FormEvent } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

const FEATURE_ICONS = ['ri-time-line', 'ri-map-pin-2-line', 'ri-calendar-check-line', 'ri-team-line'];

export default function ConsultationForm() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [charCount, setCharCount] = useState(0);
  const ref = useScrollReveal<HTMLDivElement>();

  const servicesOptions = t('consultation.servicesOptions', { returnObjects: true }) as string[];
  const truckTypes = t('consultation.truckTypes', { returnObjects: true }) as string[];
  const roleOptions = t('consultation.roleOptions', { returnObjects: true }) as string[];
  const features = t('consultation.features', { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  const toggleService = (s: string) => {
    setSelectedServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

 const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setError('');

  const form = e.currentTarget;
  const data = new FormData(form);

  // Validate message length
  const message = data.get('message') as string;
  if (message && message.length > 500) {
    setError(t('consultation.errorTooLong'));
    return;
  }

  // Add Web3Forms access key
  data.append('access_key', 'bb2487e0-c3fc-449d-ae7e-3cf19235d591');

  // Email subject/details
  data.append('subject', 'New Consultation Request - My Food Truck Agency');
  data.append('from_name', 'My Food Truck Agency Website');

  // Add selected services
  data.set('services_interested', selectedServices.join(', '));

  setLoading(true);

  try {
    const res = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: data,
    });

    const result = await res.json();

    if (result.success) {
      setSubmitted(true);
      form.reset();
      setSelectedServices([]);
      setCharCount(0);
    } else {
      setError(t('consultation.errorGeneric'));
    }
  } catch {
    setError(t('consultation.errorNetwork'));
  } finally {
    setLoading(false);
  }
};

  return (
    <section id="consultation" className="py-24 bg-teal-50">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="reveal-on-scroll animate-fade-in-up grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left Info */}
          <div>
            <span className="inline-block bg-teal-50 text-teal-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              {t('consultation.tag')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {t('consultation.title1')}<br />
              <span className="text-teal-500">{t('consultation.title2')}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">
              {t('consultation.subtitle')}
            </p>

            <div className="flex flex-col gap-5">
              {features.map((item, i) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 text-teal-600 rounded-xl flex-shrink-0">
                    <i className={`${FEATURE_ICONS[i]} text-lg`} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                    <div className="text-gray-500 text-sm">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 relative rounded-2xl overflow-hidden h-56">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20food%20truck%20business%20consultant%20meeting%20with%20client%20at%20modern%20bright%20office%20desk%20reviewing%20paperwork%20and%20permit%20documents%2C%20natural%20daylight%20through%20windows%2C%20clean%20minimalist%20workspace%20with%20laptop%20and%20documents%2C%20friendly%20professional%20atmosphere%2C%20white%20walls%20with%20green%20plants&width=700&height=350&seq=consult3&orientation=landscape"
                alt="Professional consultation in bright modern office"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <p className="text-white font-semibold text-sm">{t('consultation.imageCaption1')}</p>
                <p className="text-white/70 text-xs">{t('consultation.imageCaption2')}</p>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 text-green-600 rounded-full mb-6">
                  <i className="ri-checkbox-circle-fill text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('consultation.successTitle')}</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                  {t('consultation.successText')}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-teal-500 text-sm font-semibold hover:underline cursor-pointer"
                >
                  {t('consultation.submitAnother')}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="consultation-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('consultation.formTitle')}</h3>
                <p className="text-gray-400 text-sm -mt-3 mb-2">{t('consultation.formSubtitle')}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      {t('consultation.firstName')}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="Maria"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      {t('consultation.lastName')}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Garcia"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      {t('consultation.businessName')}
                    </label>
                    <input
                      type="text"
                      name="business_name"
                      required
                      placeholder="Taco Fuego OC"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      {t('consultation.roleLabel')}
                    </label>
                    <select
                      name="role"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-teal-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">{t('consultation.rolePlaceholder')}</option>
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    {t('consultation.emailLabel')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    {t('consultation.truckStatusLabel')}
                  </label>
                  <select
                    name="truck_type"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-teal-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">{t('consultation.truckPlaceholder')}</option>
                    {truckTypes.map((tItem) => (
                      <option key={tItem} value={tItem}>{tItem}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {t('consultation.servicesLabel')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {servicesOptions.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => toggleService(s)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                          selectedServices.includes(s)
                            ? 'bg-teal-500 text-white border-teal-500'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    {t('consultation.preferredDate')}
                  </label>
                  <input
                    type="datetime-local"
                    name="preferred_date"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    {t('consultation.conceptLabel')}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder={t('consultation.conceptPlaceholder')}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 transition-colors resize-none"
                  />
                  <p className={`text-right text-xs mt-1 ${charCount > 450 ? 'text-rose-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white font-semibold text-sm py-4 rounded-xl transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t('consultation.submitting')}
                    </>
                  ) : (
                    <>
                      {t('consultation.bookSession')}
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
                <p className="text-center text-gray-400 text-xs">
                  {t('consultation.noSpam')}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
